'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw,
  Wifi,
  WifiOff,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Server,
  Database,
  ArrowUpDown,
  XCircle,
} from 'lucide-react';

interface SyncLogEntry {
  id: string;
  officerId: string;
  syncType: string;
  recordCount: number;
  successCount: number;
  failCount: number;
  syncedAt: string;
}

interface SyncStats {
  totalVerifications: number;
  syncedCount: number;
  pendingCount: number;
  lastSyncTime: string | null;
}

export function SyncStatusPanel() {
  const { token, user } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();
  const { syncNow, pendingCount: hookPendingCount, lastSyncTime, syncCount, syncEnabled, syncFrequencyMinutes } = useOfflineSync();

  const [syncStats, setSyncStats] = useState<SyncStats>({
    totalVerifications: 0,
    syncedCount: 0,
    pendingCount: 0,
    lastSyncTime: null,
  });
  const [syncLogs, setSyncLogs] = useState<SyncLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const boothId = user?.boothId || '';

  // Load sync stats
  const loadSyncStats = useCallback(async () => {
    try {
      await offlineDB.init();

      const verifications = await offlineDB.getVerifications(boothId);
      const totalVerifications = verifications.length;
      const syncedCount = verifications.filter((v) => v.synced === 1).length;
      const pendingCount = verifications.filter((v) => v.synced === 0).length;

      // Get last sync time from synced verifications
      const syncedVerifications = verifications
        .filter((v) => v.synced === 1 && v.syncedAt)
        .sort((a, b) => new Date(b.syncedAt!).getTime() - new Date(a.syncedAt!).getTime());
      const lastSync = syncedVerifications.length > 0 ? syncedVerifications[0].syncedAt : null;

      setSyncStats({
        totalVerifications,
        syncedCount,
        pendingCount,
        lastSyncTime: lastSync,
      });
    } catch (error) {
      console.error('Failed to load sync stats:', error);
    }
  }, [boothId]);

  // Load sync logs from API if online
  const loadSyncLogs = useCallback(async () => {
    if (!isOnline || !token || !boothId) return;

    try {
      const res = await fetch(`/api/sync-status?boothId=${boothId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          // Update stats from server
          setSyncStats((prev) => ({
            ...prev,
            totalVerifications: data.totalVerifications || prev.totalVerifications,
            syncedCount: data.syncedVerifications || prev.syncedCount,
            pendingCount: data.pendingVerifications || prev.pendingCount,
            lastSyncTime: data.lastSync?.syncedAt || prev.lastSyncTime,
          }));

          // Set sync logs
          if (data.syncLogs) {
            setSyncLogs(data.syncLogs);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load sync logs from API:', error);
    }
  }, [isOnline, token, boothId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await loadSyncStats();
      await loadSyncLogs();
      setIsLoading(false);
    };

    if (boothId) {
      loadData();
    }
  }, [boothId, loadSyncStats, loadSyncLogs]);

  // Auto-refresh every 30 seconds when online
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(async () => {
      await loadSyncStats();
      await loadSyncLogs();
    }, 30_000);

    return () => clearInterval(interval);
  }, [isOnline, loadSyncStats, loadSyncLogs]);

  // Handle sync now
  const handleSyncNow = useCallback(async () => {
    if (!isOnline) return;

    setIsSyncing(true);
    setSyncMessage(null);

    try {
      const result = await syncNow();
      if (result.success && result.syncedCount > 0) {
        setSyncMessage({ type: 'success', text: `सिंक सफल! ${result.syncedCount} रिकॉर्ड सिंक हो गए।` });
        // Refresh stats
        await loadSyncStats();
        await loadSyncLogs();
      } else if (result.failedCount > 0) {
        setSyncMessage({ type: 'error', text: `${result.failedCount} रिकॉर्ड सिंक विफल। कृपया पुनः प्रयास करें।` });
      } else {
        setSyncMessage({ type: 'success', text: 'सभी डेटा पहले से सिंक है।' });
      }
    } catch {
      setSyncMessage({ type: 'error', text: 'सिंक में त्रुटि हुई।' });
    } finally {
      setIsSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    }
  }, [isOnline, syncNow, loadSyncStats, loadSyncLogs]);

  // Format time helper
  const formatTime = (time: string | null) => {
    if (!time) return 'कभी नहीं';
    try {
      const date = new Date(time);
      return date.toLocaleString('hi-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return time;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-emerald-800 font-medium">सिंक स्थिति लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-6 h-6 text-emerald-700" />
          <div>
            <h2 className="text-lg font-bold text-emerald-900">सिंक स्थिति</h2>
            <p className="text-xs text-gray-500">Sync Status Panel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={
              isOnline
                ? 'border-green-300 text-green-700 bg-green-50'
                : 'border-red-300 text-red-700 bg-red-50'
            }
          >
            {isOnline ? (
              <Wifi className="w-3 h-3 mr-1" />
            ) : (
              <WifiOff className="w-3 h-3 mr-1" />
            )}
            {isOnline ? 'ऑनलाइन' : 'ऑफलाइन'}
          </Badge>
          <Button
            onClick={() => {
              loadSyncStats();
              loadSyncLogs();
            }}
            variant="outline"
            size="sm"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            रीफ्रेश
          </Button>
        </div>
      </div>

      {/* Network Status Card */}
      <Card className={`border-2 ${isOnline ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isOnline ? 'bg-green-100' : 'bg-red-100'}`}>
              {isOnline ? (
                <Wifi className="w-7 h-7 text-green-600" />
              ) : (
                <WifiOff className="w-7 h-7 text-red-600" />
              )}
            </div>
            <div>
              <p className={`text-lg font-bold ${isOnline ? 'text-green-800' : 'text-red-800'}`}>
                {isOnline ? 'ऑनलाइन - सर्वर से कनेक्टेड' : 'ऑफलाइन - सर्वर अनुपलब्ध'}
              </p>
              <p className="text-sm text-gray-500">
                {isOnline
                  ? 'सिंक ऑपरेशन उपलब्ध है। डेटा सर्वर पर भेजा जा सकता है।'
                  : 'इंटरनेट कनेक्शन नहीं है। ऑनलाइन होने पर सिंक स्वचालित होगा।'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SyncStatCard
          icon={<Database className="w-5 h-5 text-emerald-600" />}
          label="कुल सत्यापन"
          labelEn="Total Verifications"
          value={syncStats.totalVerifications}
          bgColor="bg-emerald-50"
          borderColor="border-emerald-200"
          textColor="text-emerald-800"
        />
        <SyncStatCard
          icon={<CheckCircle2 className="w-5 h-5 text-green-600" />}
          label="सिंक पूर्ण"
          labelEn="Synced"
          value={syncStats.syncedCount}
          bgColor="bg-green-50"
          borderColor="border-green-200"
          textColor="text-green-800"
        />
        <SyncStatCard
          icon={<AlertTriangle className="w-5 h-5 text-amber-600" />}
          label="लंबित सिंक"
          labelEn="Pending Sync"
          value={syncStats.pendingCount}
          bgColor="bg-amber-50"
          borderColor="border-amber-200"
          textColor="text-amber-800"
        />
        <SyncStatCard
          icon={<Clock className="w-5 h-5 text-blue-600" />}
          label="अंतिम सिंक"
          labelEn="Last Sync"
          value={formatTime(syncStats.lastSyncTime || lastSyncTime)}
          bgColor="bg-blue-50"
          borderColor="border-blue-200"
          textColor="text-blue-800"
          isText
        />
      </div>

      {/* Sync Progress Bar */}
      {syncStats.totalVerifications > 0 && (
        <Card className="border-emerald-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800">सिंक प्रगति</span>
              <span className="text-sm font-bold text-emerald-700">
                {syncStats.totalVerifications > 0
                  ? Math.round((syncStats.syncedCount / syncStats.totalVerifications) * 100)
                  : 0}
                %
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-emerald-500 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${syncStats.totalVerifications > 0 ? (syncStats.syncedCount / syncStats.totalVerifications) * 100 : 0}%`,
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>सिंक: {syncStats.syncedCount}</span>
              <span>कुल: {syncStats.totalVerifications}</span>
              <span>लंबित: {syncStats.pendingCount}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sync Now Button */}
      <Card className="border-emerald-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">मैनुअल सिंक</p>
              <p className="text-sm text-gray-500">
                {isOnline
                  ? 'सभी लंबित सत्यापन सर्वर पर भेजें'
                  : 'ऑनलाइन होने पर ही सिंक संभव है'}
              </p>
            </div>
            <Button
              onClick={handleSyncNow}
              disabled={!isOnline || isSyncing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px] min-w-[140px]"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  सिंक हो रहा है...
                </>
              ) : (
                <>
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  अभी सिंक करें
                </>
              )}
            </Button>
          </div>

          {/* Sync Message */}
          {syncMessage && (
            <div
              className={`mt-3 flex items-center gap-2 p-3 rounded-lg text-sm ${
                syncMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {syncMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 flex-shrink-0" />
              )}
              <span>{syncMessage.text}</span>
            </div>
          )}

          {/* Offline Warning */}
          {!isOnline && syncStats.pendingCount > 0 && (
            <div className="mt-3 flex items-center gap-2 p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200">
              <WifiOff className="w-4 h-4 flex-shrink-0" />
              <span>ऑनलाइन होने पर {syncStats.pendingCount} रिकॉर्ड स्वचालित रूप से सिंक होंगे</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync History Table */}
      <Card className="border-emerald-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-base text-emerald-800">सिंक इतिहास</CardTitle>
          </div>
          <p className="text-xs text-gray-500 mt-1">हाल के सिंक ऑपरेशन का विवरण</p>
        </CardHeader>
        <CardContent>
          {syncLogs.length === 0 ? (
            <div className="text-center py-8">
              <Server className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm font-medium">कोई सिंक इतिहास नहीं मिला</p>
              <p className="text-gray-400 text-xs mt-1">पहला सिंक ऑपरेशन करने के बाद यहाँ विवरण दिखेगा</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-64 overflow-y-auto styled-scrollbar">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                    <TableHead className="text-emerald-900 font-semibold text-xs">दिनांक / समय</TableHead>
                    <TableHead className="text-emerald-900 font-semibold text-xs text-center">रिकॉर्ड</TableHead>
                    <TableHead className="text-emerald-900 font-semibold text-xs text-center">सफल</TableHead>
                    <TableHead className="text-emerald-900 font-semibold text-xs text-center">विफल</TableHead>
                    <TableHead className="text-emerald-900 font-semibold text-xs text-center">स्थिति</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-gray-50/50">
                      <TableCell className="text-xs">
                        {formatTime(log.syncedAt)}
                      </TableCell>
                      <TableCell className="text-xs text-center font-medium">
                        {log.recordCount}
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        <span className="text-green-700 font-medium">{log.successCount}</span>
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        <span className={log.failCount > 0 ? 'text-red-700 font-medium' : 'text-gray-400'}>
                          {log.failCount}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        {log.failCount === 0 ? (
                          <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50 text-xs">
                            सफल
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-xs">
                            आंशिक
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto Refresh Info */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-400 py-2">
        <Clock className="w-3 h-3" />
        {syncEnabled ? (
          <span>स्वचालित सिंक हर {syncFrequencyMinutes >= 60 ? `${Math.floor(syncFrequencyMinutes / 60)} घंटा` : `${syncFrequencyMinutes} मिनट`} | पैनल हर 30 सेकंड रीफ्रेश</span>
        ) : (
          <span>स्वचालित सिंक अक्षम (अडमिन द्वारा) | मैन्युअल सिंक उपलब्ध</span>
        )}
      </div>

      {/* Back Button */}
      <div className="flex justify-center pt-2 pb-4">
        <Button
          variant="outline"
          onClick={() => setCurrentView('officer-dashboard')}
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          वापस जाएं
        </Button>
      </div>
    </div>
  );
}

// Sync stat card component
function SyncStatCard({
  icon,
  label,
  labelEn,
  value,
  bgColor,
  borderColor,
  textColor,
  isText = false,
}: {
  icon: React.ReactNode;
  label: string;
  labelEn: string;
  value: number | string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  isText?: boolean;
}) {
  return (
    <Card className={`${borderColor} ${bgColor} shadow-sm`}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <div>
            <p className="text-xs font-medium text-gray-700">{label}</p>
            <p className="text-[10px] text-gray-400">{labelEn}</p>
          </div>
        </div>
        <p className={`text-xl font-bold ${textColor} ${isText ? 'text-sm' : ''}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
